/* tslint:disable max-classes-per-file */

import * as R from "ramda";
import * as React from "react";
import { FaPlus, FaTrash } from "react-icons/fa";
import styled, { css } from "styled-components";

import { ResolvableType } from "../../../api/ResolveApi/resolve-api.types";
import { AddButton } from "../../../App/Common/Components/FormComponents";
import { IAccount, IClass, IClassGoalResponse } from "../../../App/types/common";
import { PageContextProvider } from "../../../context/numbered-pagination";
import { dependencies } from "../../../dependencies";
import { toTuple } from "../../../parsers/resolve";
import { pluralize } from "../../../utils";
import { Header as H1 } from "../../utils/Headings";
import { usePagination } from "../../utils/Pagination/hooks";
import { ActionRow, BottomPagination, Count } from "../../utils/Pagination/NumberedPagination/common";
import { Table } from "../../utils/Pagination/NumberedPagination/table";
import ActionConfirmationButton from "../CourseAuthor/ActionConfirmation";
import { Link } from "../utils";

const { api } = dependencies;

export interface IClassesProps {
  accountId: string;
  classes: IClass[];
  deleteClass: (classId: string) => () => void;
  addNewClass: () => void;
}

const PageContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const ClassesContainer = styled.div`
  width: 100%;
`;

const Entry = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  padding: 0.5rem;
  cursor: pointer;
`;

const CenteredCell = styled.div`
  text-align: center;
`;

const fixedSize = css`
  width: 15%;
`;

const centerHeader = css`
  width: 20%;
  text-align: center;
`;

const CustomAddButton = styled(AddButton)`
  width: 15rem;
`;

const CustomActionRow = styled(ActionRow)`
  flex-direction: row;
`;

interface IClassTable {
  actions: React.ReactNode;
  goals: React.ReactNode;
  managers: React.ReactNode;
  members: React.ReactNode;
  name: React.ReactNode;
}

const maxItemsToDisplay = 2;

const ItemsList: React.FC<{ titles: string[]; itemsName: string }> = ({ titles, itemsName }) => {
  const total = titles.length;

  if (total === 0) {
    return <CenteredCell>No {itemsName}</CenteredCell>;
  }

  const itemsToDisplay = R.take(maxItemsToDisplay, titles);
  const remainingCount = total - maxItemsToDisplay;

  return (
    <CenteredCell>
      {itemsToDisplay.map((title, i) => (
        <div key={i}>{title}</div>
      ))}
      {remainingCount > 0 && <div>+{remainingCount} more</div>}
    </CenteredCell>
  );
};

const extractIds = (extractor: (aClass: IClass) => Array<[ResolvableType, string]>) => (classes: IClass[]) =>
  Array.from(new Set(R.chain(extractor, classes)));

const getAccountIds = extractIds(({ admins, members }) =>
  R.concat(admins as string[], members as string[]).map(toTuple)
);

const getCourseIds = extractIds(({ goals }) =>
  R.take(
    maxItemsToDisplay,
    goals.elems.filter(({ deleted }) => !deleted).map(({ course }) => [ResolvableType.Course, course])
  )
);

const toId = ([resolveType, uuid]: [ResolvableType, string]) => `${resolveType}.${uuid}`;

const handleFetchDisplayInfo = async (accountId: string, allIds: Array<[ResolvableType, string]>) => {
  const [error, resp] = await api.resolve.displayInfo.post(accountId, allIds);

  if (error || !resp) {
    return Promise.reject(error);
  }

  const allIdsToUnify = allIds.map(toId);
  const unifiedParticipantsWithIds = resp.data.map((item, index) => ({
    ...item?.value,
    id: allIdsToUnify[index],
  })) as IAccount[];

  return unifiedParticipantsWithIds;
};

const updateClassArray = (aClass: IClass, participantId: string, participant: IAccount) => {
  aClass[participantId] = aClass[participantId].filter((id: string) => id !== participant.id);
  aClass[participantId].push(participant);
  return aClass;
};

const getClassesWithUpdatedInfo = (classes: IClass[], unifiedParticipantsWithIds: IAccount[]) => {
  return classes.map(originalClass => {
    let aClass = { ...originalClass };
    unifiedParticipantsWithIds.forEach(participant => {
      if ((aClass.admins as string[]).includes(participant.id)) {
        aClass = updateClassArray(aClass, "admins", participant);
      }
      if ((aClass.members as string[]).includes(participant.id)) {
        aClass = updateClassArray(aClass, "members", participant);
      }
      if (aClass.goals.elems.some(({ course }) => course === participant.id.slice(4))) {
        aClass.goals.elems = aClass.goals.elems.filter(({ course }) => course !== participant.id.slice(4));
        aClass.goals.elems.push({
          ...((participant as unknown) as IClassGoalResponse),
          course: participant.id.slice(4),
        });
      }
    });
    return aClass;
  });
};

const Classes: React.FC<IClassesProps> = props => {
  const { accountId, classes } = props;
  const [classesState, setClassesState] = React.useState(classes);
  const { value, pagedData } = usePagination(classesState);

  React.useEffect(() => {
    const courseIds = getCourseIds(classes);
    const accountIds = getAccountIds(classes);
    const allIds = R.concat(courseIds, accountIds);

    handleFetchDisplayInfo(accountId, allIds)
      .then(unifiedParticipantsWithIds =>
        setClassesState(getClassesWithUpdatedInfo(classes, unifiedParticipantsWithIds))
      )
      .catch(e => e);
  }, [value.pageIndex, value.perPage]);

  const toClassTable = (aClass: IClass): IClassTable => ({
    actions: (
      <ActionConfirmationButton action="Delete" actionFn={props.deleteClass(aClass.classId)} ActionIcon={FaTrash} />
    ),
    goals: (
      <ItemsList
        itemsName={"goals"}
        titles={aClass.goals.elems.filter(({ deleted }) => !deleted).map(goal => goal.displayName)}
      />
    ),
    managers: (
      <ItemsList itemsName={"managers"} titles={(aClass.admins as IAccount[]).map(admin => admin.displayName)} />
    ),
    members: (
      <ItemsList
        itemsName={"members"}
        titles={(aClass.members as IAccount[]).filter(({ deleted }) => !deleted).map(member => member.displayName)}
      />
    ),
    name: (
      <Entry>
        <Link to={`/class-management/${aClass.classId}`}>{aClass.displayName}</Link>
      </Entry>
    ),
  });

  return (
    <PageContainer>
      <PageContextProvider value={value}>
        <H1>Classes</H1>
        <ClassesContainer>
          <CustomActionRow>
            <CustomAddButton onClick={props.addNewClass}>
              <FaPlus /> Create New Class
            </CustomAddButton>
            <Count>{pluralize("class", props.classes.length, "es")}</Count>
          </CustomActionRow>
          <Table
            columns={[
              { name: "Name", accessor: "name", styles: fixedSize },
              { name: "Goals", accessor: "goals", styles: centerHeader },
              { name: "Members", accessor: "members", styles: centerHeader },
              { name: "Managers", accessor: "managers", styles: centerHeader },
              { name: "Actions", accessor: "actions" },
            ]}
            data={pagedData.map(toClassTable)}
          />
        </ClassesContainer>
        <BottomPagination total={classes.length} />
      </PageContextProvider>
    </PageContainer>
  );
};

export default Classes;
